import React from 'react';
import { motion } from 'framer-motion';
import { Smartphone, ExternalLink, Github, Globe } from 'lucide-react';
import { projectsData } from '../data/mock';

const ProjectsSection = () => {
  return (
    <section id="portfolio" className="py-32 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="text-[#00ff88] text-sm font-medium uppercase tracking-wider mb-4 block">
            Portfolio
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Featured <span className="gradient-text">Projects</span>
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto">
            A showcase of applications and security tools I've built.
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div className="space-y-12">
          {projectsData.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bento-card overflow-hidden"
            >
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-0 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                {/* Project Info */}
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  {/* Logo & Title */}
                  <div className="flex items-center gap-4 mb-6">
                    {project.logo ? (
                      <img
                        src={project.logo}
                        alt={project.name}
                        className="w-14 h-14 object-contain rounded-xl"
                      />
                    ) : (
                      <div className="w-14 h-14 bg-gradient-to-br from-[#00ff88] to-[#00ccff] rounded-xl flex items-center justify-center">
                        <Globe size={24} className="text-black" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-display text-2xl font-bold text-white">
                        {project.name}
                      </h3>
                      <span className="text-[#00ff88] text-sm">{project.category}</span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-white/60 mb-6 leading-relaxed">
                    {project.description}
                  </p>

                  {/* Technologies */}
                  <div className="mb-6">
                    <span className="text-white/30 text-xs uppercase tracking-wider mb-3 block">
                      Technologies
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, tIndex) => (
                        <span key={tIndex} className="tech-tag">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mb-8">
                    <span className="text-white/30 text-xs uppercase tracking-wider mb-3 block">
                      Key Features
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {project.features.map((feature, fIndex) => (
                        <span key={fIndex} className="skill-tag text-xs">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Links */}
                  <div className="flex gap-4">
                    <motion.a
                      href={project.link}
                      className="btn-primary text-sm"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      View Project
                      <ExternalLink size={16} />
                    </motion.a>
                  </div>
                </div>

                {/* Project Visual */}
                <div className="relative bg-gradient-to-br from-[#111] to-[#0a0a0a] p-8 md:p-12 flex items-center justify-center min-h-[400px]">
                  {project.screenshot ? (
                    <motion.div
                      className="relative"
                      initial={{ scale: 0.9, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      {/* Phone Frame */}
                      <div className="relative max-w-[280px] mx-auto">
                        <div className="bg-[#1a1a1a] rounded-[40px] p-3 shadow-2xl border border-white/10">
                          <div className="bg-black rounded-[32px] overflow-hidden relative">
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-b-xl z-10" />
                            <img
                              src={project.screenshot}
                              alt={`${project.name} Screenshot`}
                              className="w-full h-auto"
                            />
                          </div>
                        </div>
                        {/* Glow */}
                        <div className="absolute -inset-8 bg-[#00ff88]/10 rounded-[60px] blur-3xl -z-10" />
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      className="text-center"
                      initial={{ scale: 0.9, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      <div className="w-32 h-32 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-[#00ff88]/20 to-[#00ccff]/20 border border-[#00ff88]/30 flex items-center justify-center">
                        <Globe size={48} className="text-[#00ff88]" />
                      </div>
                      <p className="text-white/30 text-sm">Security Tool</p>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
